import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import api from '../../lib/axios'
import { PageHeader, Card, Spinner, EmptyState, Button } from '../../components/ui'
import toast from 'react-hot-toast'

function ProjectCard({ project, onDelete }) {
  return (
    <Link to={`/dashboard/projects/${project.id}`}
      className="glass-card-hover p-6 block group">
      <div className="flex items-start justify-between mb-4">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center text-lg flex-shrink-0"
          style={{ background: `${project.color}22`, border: `1px solid ${project.color}44` }}>
          ⬡
        </div>
        <span className="text-[10px] text-white/30 font-mono">{project.repositories_count} repos</span>
      </div>
      <h3 className="font-semibold text-white group-hover:text-purple-300 transition-colors mb-1">{project.name}</h3>
      {project.description && <p className="text-xs text-white/40 line-clamp-2 mb-4">{project.description}</p>}
      <div className="flex items-center gap-4 text-[11px] text-white/30">
        <span>📄 {project.documents_count ?? 0}</span>
        <span>🧪 {project.test_plans_count ?? 0}</span>
        <span>◈ {project.specifications_count ?? 0}</span>
      </div>
    </Link>
  )
}

function CreateModal({ onClose, onCreated }) {
  const [form, setForm] = useState({ name: '', description: '', color: '#7C3AED' })
  const [loading, setLoading] = useState(false)
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }))

  const handleSubmit = async e => {
    e.preventDefault()
    setLoading(true)
    try {
      const { data } = await api.post('/projects', form)
      toast.success('Project created!')
      onCreated(data)
    } catch { toast.error('Failed to create project.') }
    finally { setLoading(false) }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative glass-card w-full max-w-md p-7 animate-fade-in">
        <h2 className="font-display font-bold text-lg text-white mb-6">New Project</h2>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="label-specze">Project name *</label>
            <input className="input-specze" required autoFocus value={form.name} onChange={set('name')} placeholder="My App" />
          </div>
          <div>
            <label className="label-specze">Description</label>
            <textarea className="input-specze resize-none h-20" value={form.description} onChange={set('description')} placeholder="What is this project about?" />
          </div>
          <div>
            <label className="label-specze">Color</label>
            <div className="flex gap-2 flex-wrap">
              {['#7C3AED','#2563EB','#0891B2','#059669','#D97706','#DC2626','#DB2777'].map(c => (
                <button key={c} type="button" onClick={() => setForm(f => ({ ...f, color: c }))}
                  className="w-7 h-7 rounded-full transition-transform hover:scale-110"
                  style={{ background: c, ring: form.color === c ? `2px solid white` : 'none',
                    boxShadow: form.color === c ? `0 0 0 2px #fff, 0 0 0 4px ${c}` : 'none' }} />
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-2">
            <Button type="submit" disabled={loading} className="flex-1">
              {loading ? 'Creating…' : 'Create Project'}
            </Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function Projects() {
  const [projects, setProjects] = useState([])
  const [loading, setLoading]   = useState(true)
  const [showModal, setShowModal] = useState(false)

  useEffect(() => {
    api.get('/projects').then(r => setProjects(r.data)).finally(() => setLoading(false))
  }, [])

  return (
    <div className="pb-10 animate-fade-in" style={{ background: 'linear-gradient(180deg, #0d0521 0%, #07020f 100%)', minHeight: '100%' }}>
      <PageHeader title="Projects" subtitle={`${projects.length} active projects`}>
        <Button onClick={() => setShowModal(true)}>+ New Project</Button>
      </PageHeader>

      {showModal && (
        <CreateModal onClose={() => setShowModal(false)} onCreated={p => { setProjects(prev => [p, ...prev]); setShowModal(false) }} />
      )}

      <div className="px-8">
        {loading ? (
          <div className="flex items-center justify-center h-48"><Spinner size="lg" /></div>
        ) : projects.length === 0 ? (
          <Card>
            <EmptyState icon="⬡" title="No projects yet"
              description="Create your first project to start connecting repositories."
              action={<Button onClick={() => setShowModal(true)}>Create First Project</Button>} />
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {projects.map(p => <ProjectCard key={p.id} project={p} />)}
          </div>
        )}
      </div>
    </div>
  )
}
